import {
  useCallback,
  useMemo,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import {
  useMatchLogStore,
  type MatchEvent,
} from "../../../store/useMatchLogStore";
import type { Player, Match, StaffMember } from "../types";
import type { CardSelection } from "../components/molecules/QuickCardPanel";
import {
  addMillisecondsToClock,
  compareCardEventOrder,
  getActiveYellowCountForPlayer,
} from "../lib/clockHelpers";
import { sameTeamId } from "../utils/teamId";
import { dedupeByStableIdentity } from "./useLogIntegrityChecks";

/**
 * Staff-subject equivalent of `getActiveYellowCountForPlayer` (clockHelpers.ts,
 * player-only). Kept local rather than widening that helper's filter, since
 * clockHelpers.ts is outside this file's ownership for this wave — mirrors
 * its cancel/second-yellow bookkeeping exactly, keyed on `staff_id`.
 */
const getActiveYellowCountForStaff = (
  events: MatchEvent[],
  staffId: string,
): number => {
  let yellow = 0;
  let red = 0;

  events
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => event.type === "Card" && event.staff_id === staffId)
    .sort(compareCardEventOrder)
    .forEach(({ event }) => {
      const cardType = String(event.data?.card_type || "").toLowerCase();

      if (cardType.includes("cancel")) {
        if (red > 0 && yellow >= 2) {
          red -= 1;
          yellow -= 1;
        } else if (red > 0) {
          red -= 1;
        } else if (yellow > 0) {
          yellow -= 1;
        }
        return;
      }

      if (cardType.includes("yellow")) {
        yellow += 1;
        return;
      }

      if (cardType.includes("red")) {
        red += 1;
      }
    });

  return yellow;
};

interface DestinationPayload {
  xPercent: number;
  yPercent: number;
  statsbomb: [number, number];
  isOutOfBounds: boolean;
  outOfBoundsEdge?: "left" | "right" | "top" | "bottom" | null;
}

interface DestinationResult {
  sent?: boolean;
  outOfBounds?: boolean;
  blockedInteriorPassDestination?: boolean;
  isGoal?: boolean;
  triggerContext?: {
    teamId: string;
    playerId?: string;
    actionType: string;
  } | null;
}

interface UseCockpitInteractionHandlersParams {
  t: (...args: any[]) => any;
  match: Match | null;
  cockpitLocked: boolean;
  selectedTeam: "home" | "away" | "both";
  setSelectedTeam: (team: "home" | "away" | "both") => void;
  pendingCardType: CardSelection | null;
  pendingCardTypeRef: MutableRefObject<CardSelection | null>;
  setPendingCardType: Dispatch<SetStateAction<CardSelection | null>>;
  resetFlow: () => void;
  operatorPeriod: number;
  // PMA-COCK-086 broadcast-cap value (formatMatchClock(broadcastClock.mainSeconds)
  // in CockpitContext.tsx), NOT the raw accumulating global clock. Card events
  // were stamped off the raw clock until this fix; in the second half the raw
  // clock carries first-half stoppage (period_timestamps["2"].global_start_seconds)
  // forward, which the broadcast clock deliberately does not (PMA-COCK-017 /
  // PMA-COCK-086 AC6). Deliberately NOT named `matchClock` (useActionFlow.ts's
  // equivalent param) — `buildCardPayload` below already has its own local
  // `matchClock` parameter, and naming this the same would make its
  // `matchClock = matchClock` default self-referential (TDZ error).
  broadcastMatchClock: string;
  // client_id excluded to match useMatchSocket.ts's sendEvent -- see that
  // file's UseMatchSocketReturn interface for why.
  sendEvent: (event: Omit<any, "match_id" | "timestamp" | "client_id">) => void;
  expelledPlayerIds: Set<string>;
  isVarActive: boolean;
  currentStep: string;
  selectedPlayer: Player | null;
  selectedAction: string | null;
  currentTeam: { id: string; players: Player[] } | undefined;
  onFieldIds: { home: Set<string>; away: Set<string> };
  setSubstitutionTeam: (team: "home" | "away") => void;
  setShowSubstitutionFlow: (open: boolean) => void;
  setSubstitutionInitialPlayerOn?: (player: Player | null) => void;
  setSubstitutionInitialPlayerOff?: (player: Player | null) => void;
  setPriorityPlayerId: (playerId: string | null) => void;
  handlePlayerClick: (
    player: Player,
    anchor?: { xPercent: number; yPercent: number },
    location?: [number, number],
  ) => void;
  handleDestinationClick: (args: {
    destination: DestinationPayload;
    targetPlayer?: Player;
  }) => DestinationResult | undefined;
  handleActionClick: (action: string) => void;
  handleOutcomeClick: (outcome: string) => void;
  handleRecipientClick: (player: Player) => void;
  beginIneffective: (note?: string | null, context?: any) => void;
  showToast: (message: string) => void;
}

interface UseCockpitInteractionHandlersResult {
  handleCardSelection: (cardType: CardSelection) => void;
  cancelCardSelection: () => void;
  handlePlayerSelection: (player: Player) => void;
  /**
   * Reused unchanged by `CardSubjectModal` (PMA-COCK-120) — the modal calls
   * this directly, not through `handlePlayerSelection`, since it already
   * knows the pending card type contextually. Its `useMatchLogStore.getState()`
   * click-time read (PMA-COCK-113 invariant) must stay exactly as-is.
   */
  logCardForPlayer: (
    player: Player,
    cardType: CardSelection,
    location?: [number, number],
  ) => void;
  /** Reused unchanged by `CardSubjectModal` (PMA-COCK-120) — see `logCardForPlayer` above. */
  logCardForStaff: (staff: StaffMember, cardType: CardSelection) => void;
  handleFieldPlayerSelection: (
    player: Player,
    anchor: { xPercent: number; yPercent: number },
    location: [number, number],
    side: "home" | "away",
  ) => void;
  handleFieldDestination: (destination: DestinationPayload) => void;
  handleQuickSubstitution: (team: "home" | "away") => void;
  handleActionClickOverride: (action: string) => void;
  handleOutcomeSelect: (outcome: string) => void;
  handleRecipientSelect: (player: Player) => void;
  eligibleRecipients: Player[];
}

export const useCockpitInteractionHandlers = ({
  t,
  match,
  cockpitLocked,
  selectedTeam,
  setSelectedTeam,
  pendingCardType,
  pendingCardTypeRef,
  setPendingCardType,
  resetFlow,
  operatorPeriod,
  broadcastMatchClock,
  sendEvent,
  expelledPlayerIds,
  isVarActive,
  currentStep,
  selectedPlayer,
  selectedAction,
  currentTeam,
  onFieldIds,
  setSubstitutionTeam,
  setShowSubstitutionFlow,
  setSubstitutionInitialPlayerOn,
  setSubstitutionInitialPlayerOff,
  setPriorityPlayerId,
  handlePlayerClick,
  handleDestinationClick,
  handleActionClick,
  handleOutcomeClick,
  handleRecipientClick,
  beginIneffective,
  showToast,
}: UseCockpitInteractionHandlersParams): UseCockpitInteractionHandlersResult => {
  const determinePlayerTeam = useCallback(
    (player: Player): "home" | "away" | null => {
      if (!match) return null;
      if (
        match.home_team.players.some(
          (homePlayer) => homePlayer.id === player.id,
        )
      ) {
        return "home";
      }
      if (
        match.away_team.players.some(
          (awayPlayer) => awayPlayer.id === player.id,
        )
      ) {
        return "away";
      }
      return null;
    },
    [match],
  );

  const getTeamSide = useCallback(
    (teamId?: string | null): "home" | "away" | null => {
      if (!match || !teamId) return null;
      if (teamId === match.home_team.id) return "home";
      if (teamId === match.away_team.id) return "away";
      return null;
    },
    [match],
  );

  const handleCardSelection = useCallback(
    (cardType: CardSelection) => {
      if (cockpitLocked) return;
      if (selectedTeam === "both") {
        setSelectedTeam("home");
      }
      setPendingCardType((prev) => {
        const next = prev === cardType ? null : cardType;
        pendingCardTypeRef.current = next;
        return next;
      });
      resetFlow();
    },
    [
      cockpitLocked,
      pendingCardTypeRef,
      resetFlow,
      selectedTeam,
      setPendingCardType,
      setSelectedTeam,
    ],
  );

  const cancelCardSelection = useCallback(() => {
    pendingCardTypeRef.current = null;
    setPendingCardType(null);
  }, [pendingCardTypeRef, setPendingCardType]);

  const logCardForPlayer = useCallback(
    (player: Player, cardType: CardSelection, location?: [number, number]) => {
      if (!match) return;
      const playerTeam = determinePlayerTeam(player);
      if (!playerTeam) return;
      const team = playerTeam === "home" ? match.home_team : match.away_team;
      const latestState = useMatchLogStore.getState();
      // PMA-COCK-205 (EPIC-43): dedupeByStableIdentity guards the one
      // remaining by-design overlap (sendEvent()'s offline branch). Without
      // it, a duplicated pre-existing Yellow followed by a real Cancelled
      // card only offsets ONE of the two identical copies, leaving a
      // phantom active yellow behind — the >=1 threshold below looks
      // immune to simple doubling, but is not immune to that
      // cancel-interaction case.
      const combinedEvents = dedupeByStableIdentity([
        ...latestState.liveEvents,
        ...latestState.queuedEvents,
      ]);
      const existingCardEventsAtClock = combinedEvents.filter(
        (event) =>
          event.type === "Card" &&
          event.period === operatorPeriod &&
          sameTeamId(event.team_id, team.id) &&
          event.player_id === player.id &&
          event.match_clock === broadcastMatchClock,
      ).length;
      const cancelClockOffsetMs = existingCardEventsAtClock + 1;

      let resolvedCard: string = cardType;
      if (cardType === "Yellow") {
        const previousYellows = getActiveYellowCountForPlayer(
          combinedEvents,
          player.id,
        );
        resolvedCard = previousYellows >= 1 ? "Yellow (Second)" : "Yellow";
      }

      const buildCardPayload = (
        cardValue: string,
        matchClock = broadcastMatchClock,
      ) => ({
        match_clock: matchClock,
        period: operatorPeriod,
        team_id: team.id,
        player_id: player.id,
        type: "Card" as const,
        data: {
          card_type: cardValue,
          reason: cardValue === "Cancelled" ? "VAR" : "Foul",
        },
        ...(location ? { location } : {}),
      });

      if (resolvedCard === "Yellow (Second)") {
        const secondYellowClock = addMillisecondsToClock(
          broadcastMatchClock,
          1,
        );
        const redClock = addMillisecondsToClock(broadcastMatchClock, 2);
        sendEvent(buildCardPayload(resolvedCard, secondYellowClock));
        sendEvent(buildCardPayload("Red", redClock));
      } else if (resolvedCard === "Cancelled") {
        const cancelledClock = addMillisecondsToClock(
          broadcastMatchClock,
          cancelClockOffsetMs,
        );
        sendEvent(buildCardPayload(resolvedCard, cancelledClock));
      } else {
        sendEvent(buildCardPayload(resolvedCard));
      }

      setPendingCardType(null);
      pendingCardTypeRef.current = null;
      resetFlow();
    },
    [
      determinePlayerTeam,
      broadcastMatchClock,
      match,
      operatorPeriod,
      pendingCardTypeRef,
      resetFlow,
      sendEvent,
      setPendingCardType,
    ],
  );

  const determineStaffTeam = useCallback(
    (staff: StaffMember): "home" | "away" | null => {
      if (!match) return null;
      if (
        match.home_team.technical_staff?.some(
          (member) => member.staff_id === staff.staff_id,
        )
      ) {
        return "home";
      }
      if (
        match.away_team.technical_staff?.some(
          (member) => member.staff_id === staff.staff_id,
        )
      ) {
        return "away";
      }
      return null;
    },
    [match],
  );

  const logCardForStaff = useCallback(
    (staff: StaffMember, cardType: CardSelection) => {
      if (!match) return;
      const staffTeam = determineStaffTeam(staff);
      if (!staffTeam) return;
      const team = staffTeam === "home" ? match.home_team : match.away_team;
      const latestState = useMatchLogStore.getState();
      // PMA-COCK-205 (EPIC-43): see logCardForPlayer's identical comment
      // above — dedupeByStableIdentity guards the cancel-interaction case,
      // not just simple doubling.
      const combinedEvents = dedupeByStableIdentity([
        ...latestState.liveEvents,
        ...latestState.queuedEvents,
      ]);
      const existingCardEventsAtClock = combinedEvents.filter(
        (event) =>
          event.type === "Card" &&
          event.period === operatorPeriod &&
          sameTeamId(event.team_id, team.id) &&
          event.staff_id === staff.staff_id &&
          event.match_clock === broadcastMatchClock,
      ).length;
      const cancelClockOffsetMs = existingCardEventsAtClock + 1;

      let resolvedCard: string = cardType;
      if (cardType === "Yellow") {
        const previousYellows = getActiveYellowCountForStaff(
          combinedEvents,
          staff.staff_id,
        );
        resolvedCard = previousYellows >= 1 ? "Yellow (Second)" : "Yellow";
      }

      const buildCardPayload = (
        cardValue: string,
        matchClock = broadcastMatchClock,
      ) => ({
        match_clock: matchClock,
        period: operatorPeriod,
        team_id: team.id,
        player_id: null,
        staff_id: staff.staff_id,
        type: "Card" as const,
        data: {
          card_type: cardValue,
          reason: cardValue === "Cancelled" ? "VAR" : "Foul",
        },
      });

      if (resolvedCard === "Yellow (Second)") {
        const secondYellowClock = addMillisecondsToClock(
          broadcastMatchClock,
          1,
        );
        const redClock = addMillisecondsToClock(broadcastMatchClock, 2);
        sendEvent(buildCardPayload(resolvedCard, secondYellowClock));
        sendEvent(buildCardPayload("Red", redClock));
      } else if (resolvedCard === "Cancelled") {
        const cancelledClock = addMillisecondsToClock(
          broadcastMatchClock,
          cancelClockOffsetMs,
        );
        sendEvent(buildCardPayload(resolvedCard, cancelledClock));
      } else {
        sendEvent(buildCardPayload(resolvedCard));
      }

      setPendingCardType(null);
      pendingCardTypeRef.current = null;
      resetFlow();
    },
    [
      determineStaffTeam,
      broadcastMatchClock,
      match,
      operatorPeriod,
      pendingCardTypeRef,
      resetFlow,
      sendEvent,
      setPendingCardType,
    ],
  );

  const handleQuickSubstitution = useCallback(
    (team: "home" | "away", player?: Player) => {
      if (cockpitLocked) return;
      setSubstitutionTeam(team);
      if (setSubstitutionInitialPlayerOn) {
        setSubstitutionInitialPlayerOn(player ?? null);
      }
      if (setSubstitutionInitialPlayerOff) {
        setSubstitutionInitialPlayerOff(null);
      }
      setShowSubstitutionFlow(true);
      resetFlow();
    },
    [
      cockpitLocked,
      resetFlow,
      setShowSubstitutionFlow,
      setSubstitutionTeam,
      setSubstitutionInitialPlayerOn,
      setSubstitutionInitialPlayerOff,
    ],
  );

  const handlePlayerSelection = useCallback(
    (player: Player) => {
      if (cockpitLocked) return;
      const activePendingCard = pendingCardTypeRef.current ?? pendingCardType;
      if (activePendingCard) {
        logCardForPlayer(player, activePendingCard);
        return;
      }
      if (expelledPlayerIds.has(player.id)) {
        showToast(
          t("playerExpelled", "Player is expelled and cannot log actions."),
        );
        return;
      }
      const playerTeam = determinePlayerTeam(player);
      if (!playerTeam) return;

      if (playerTeam !== selectedTeam) {
        setSelectedTeam(playerTeam);
      }
      handlePlayerClick(player);
    },
    [
      cockpitLocked,
      pendingCardType,
      pendingCardTypeRef,
      logCardForPlayer,
      expelledPlayerIds,
      showToast,
      t,
      determinePlayerTeam,
      selectedTeam,
      setSelectedTeam,
      handlePlayerClick,
    ],
  );

  const handleFieldPlayerSelection = useCallback(
    (
      player: Player,
      anchor: { xPercent: number; yPercent: number },
      location: [number, number],
      side: "home" | "away",
    ) => {
      if (cockpitLocked) return;

      if (isVarActive) {
        showToast(
          t(
            "varBlocksFieldActions",
            "Field actions are blocked while VAR is active.",
          ),
        );
        return;
      }

      const activePendingCard = pendingCardTypeRef.current ?? pendingCardType;
      if (
        expelledPlayerIds.has(player.id) &&
        activePendingCard !== "Cancelled"
      ) {
        showToast(
          t("playerExpelled", "Player is expelled and cannot log actions."),
        );
        return;
      }

      const isBenchPlayer =
        !onFieldIds.home.has(player.id) && !onFieldIds.away.has(player.id);

      // ── Recipient selection on the pitch ──────────────────────────────
      // When the operator needs to pick a second player (Duel / Penalty),
      // tapping an opponent dot on the field routes directly to the recipient
      // handler so they don't have to scroll to the grid below.
      if (currentStep === "selectRecipient") {
        if (isBenchPlayer) {
          showToast(
            t(
              "benchPlayerInvalidRecipient",
              "Bench players cannot be selected as action recipients.",
            ),
          );
          return;
        }
        // PMA-COCK-166: Foul also requires an OPPONENT pick (fouled player)
        // — without this, a same-team field tap during its recipient step
        // would fall through to the `else` branch below and wrongly credit
        // a teammate. That branch was previously unreachable dead code:
        // Duel/Penalty were the only two actions that ever reached
        // `selectRecipient` via a field tap before PMA-COCK-166 (Pass's own
        // needsRecipient path never enters `selectRecipient` — it resolves
        // through `selectDestination`). BlockedShot no longer reaches
        // `selectRecipient` at all as of PMA-COCK-165's field-destination
        // rework (client wave 2026-09-07) — it now resolves through
        // `selectDestination` below, same as Pass/Shot.
        const needsOpponent =
          selectedAction === "Duel" ||
          selectedAction === "Penalty" ||
          selectedAction === "Foul";
        if (needsOpponent) {
          const selectedSide =
            currentTeam?.id === match?.home_team.id ? "home" : "away";
          if (side !== selectedSide) {
            handleRecipientClick(player);
          }
        } else {
          handleRecipientClick(player);
        }
        return;
      }

      if (currentStep === "selectDestination") {
        const result = handleDestinationClick({
          destination: {
            xPercent: anchor.xPercent,
            yPercent: anchor.yPercent,
            statsbomb: location,
            isOutOfBounds: false,
            outOfBoundsEdge: null,
          },
          targetPlayer: player,
        });
        if (result?.triggerContext) {
          beginIneffective(
            result?.isGoal
              ? t("ineffectiveNoteGoal", "Goal")
              : t("ineffectiveNoteOut", "Out of bounds"),
            result?.triggerContext
              ? {
                  teamId: result.triggerContext.teamId,
                  playerId: result.triggerContext.playerId,
                  actionType: result.triggerContext.actionType,
                }
              : null,
          );
        }
        return;
      }

      if (activePendingCard) {
        logCardForPlayer(player, activePendingCard, location);
        return;
      }

      if (isBenchPlayer) {
        handleQuickSubstitution(side, player);
        return;
      }

      if (side !== selectedTeam) {
        setSelectedTeam(side);
      }
      handlePlayerClick(player, anchor, location);
    },
    [
      beginIneffective,
      cockpitLocked,
      currentStep,
      currentTeam,
      expelledPlayerIds,
      handleDestinationClick,
      handlePlayerClick,
      handleQuickSubstitution,
      handleRecipientClick,
      isVarActive,
      logCardForPlayer,
      match,
      onFieldIds,
      pendingCardType,
      pendingCardTypeRef,
      selectedAction,
      selectedTeam,
      setSelectedTeam,
      showToast,
      t,
    ],
  );

  const handleFieldDestination = useCallback(
    (destination: DestinationPayload) => {
      if (cockpitLocked || currentStep !== "selectDestination") return;
      if (isVarActive) {
        showToast(
          t(
            "varBlocksFieldActions",
            "Field actions are blocked while VAR is active.",
          ),
        );
        return;
      }
      const result = handleDestinationClick({ destination });
      if (result?.blockedInteriorPassDestination) {
        showToast(
          t(
            "passRequiresTargetOrOut",
            "Pass must go to a player or out of bounds.",
          ),
        );
        return;
      }
      if (result?.triggerContext) {
        beginIneffective(
          result?.isGoal
            ? t("ineffectiveNoteGoal", "Goal")
            : t("ineffectiveNoteOut", "Out of bounds"),
          result?.triggerContext
            ? {
                teamId: result.triggerContext.teamId,
                playerId: result.triggerContext.playerId,
                actionType: result.triggerContext.actionType,
              }
            : null,
        );
      }
    },
    [
      beginIneffective,
      cockpitLocked,
      currentStep,
      handleDestinationClick,
      isVarActive,
      showToast,
      t,
    ],
  );

  const handleActionClickOverride = useCallback(
    (action: string) => {
      if (cockpitLocked) return;
      if (action === "Substitution") {
        const teamForSub = selectedPlayer
          ? determinePlayerTeam(selectedPlayer) || "home"
          : selectedTeam === "both"
            ? "home"
            : selectedTeam;
        setSubstitutionTeam(teamForSub);
        setSubstitutionInitialPlayerOn?.(null);
        setSubstitutionInitialPlayerOff?.(null);
        setShowSubstitutionFlow(true);
        resetFlow();
      } else {
        handleActionClick(action);
      }
    },
    [
      cockpitLocked,
      determinePlayerTeam,
      handleActionClick,
      resetFlow,
      selectedPlayer,
      selectedTeam,
      setShowSubstitutionFlow,
      setSubstitutionInitialPlayerOff,
      setSubstitutionInitialPlayerOn,
      setSubstitutionTeam,
    ],
  );

  const handleOutcomeSelect = useCallback(
    (outcome: string) => {
      if (cockpitLocked) return;
      handleOutcomeClick(outcome);
    },
    [cockpitLocked, handleOutcomeClick],
  );

  const handleRecipientSelect = useCallback(
    (player: Player) => {
      if (cockpitLocked) return;
      setPriorityPlayerId(player.id);
      handleRecipientClick(player);
    },
    [cockpitLocked, handleRecipientClick, setPriorityPlayerId],
  );

  const eligibleRecipients = useMemo(() => {
    if (!currentTeam || !match) return [] as Player[];
    // Duel / Penalty / Foul flow: show opponent team players so the
    // operator picks the duel loser / fouled player. Foul added
    // PMA-COCK-166 (client wave 2026-09-07, fe-events lane) — flagged in
    // team_findings.md as a necessary edit to this file outside that lane's
    // owned set; same additive pattern as the pre-existing Duel/Penalty
    // check. BlockedShot was added the same wave (PMA-COCK-165) and REMOVED
    // here by PMA-COCK-165's field-destination rework (client wave
    // 2026-09-07) — it no longer has a `selectRecipient` step, so this
    // condition never needs to special-case it again.
    if (
      (selectedAction === "Duel" ||
        selectedAction === "Penalty" ||
        selectedAction === "Foul") &&
      currentStep === "selectRecipient"
    ) {
      const opponentTeam =
        currentTeam.id === match.home_team.id
          ? match.away_team
          : match.home_team;
      const oppSide = getTeamSide(opponentTeam.id);
      if (!oppSide) return [] as Player[];
      const oppOnField = onFieldIds[oppSide];
      return opponentTeam.players.filter((p) => oppOnField.has(p.id));
    }
    const side = getTeamSide(currentTeam.id);
    if (!side) return [] as Player[];
    const onField = onFieldIds[side];
    return currentTeam.players.filter(
      (player) => onField.has(player.id) && player.id !== selectedPlayer?.id,
    );
  }, [
    currentStep,
    currentTeam,
    getTeamSide,
    match,
    onFieldIds,
    selectedAction,
    selectedPlayer,
  ]);

  return {
    handleCardSelection,
    cancelCardSelection,
    handlePlayerSelection,
    logCardForPlayer,
    logCardForStaff,
    handleFieldPlayerSelection,
    handleFieldDestination,
    handleQuickSubstitution,
    handleActionClickOverride,
    handleOutcomeSelect,
    handleRecipientSelect,
    eligibleRecipients,
  };
};
